/* =============================================
   Studio Ode — Bespoke Quotation Flow
   Opens a review popup summarising the customer's
   selections, collects contact info, then launches
   WhatsApp with a pre-drafted message.
   No cart submission. No Shopify order.
   ============================================= */

class OdeBespokeQuote {
  constructor(root) {
    this.overlay = root;
    this.panel = root.querySelector('.ode-popup-panel');
    this.closeBtn = root.querySelector('.ode-popup-close');
    this.formState = root.querySelector('.ode-popup-form');
    this.successState = root.querySelector('.ode-confirm-msg');
    this.summaryContainer = root.querySelector('.ode-quote-summary');
    this.confirmRow = root.querySelector('.ode-confirm-row');
    this.confirmCheck = root.querySelector('.ode-confirm-checkbox');
    this.submitBtn = root.querySelector('.ode-btn-submit');
    this.nameInput = root.querySelector('[data-ode-quote-field="name"]');
    this.emailInput = root.querySelector('[data-ode-quote-field="email"]');
    this.phoneInput = root.querySelector('[data-ode-quote-field="phone"]');

    if (!this.panel || !this.formState || !this.summaryContainer) return;

    this.confirmed = false;
    this.autoCloseTimer = null;

    // All "Request Quotation" buttons on the page (there should be just one,
    // but scope by data attribute just in case).
    this.openButtons = Array.from(
      document.querySelectorAll('[data-ode-quote-open]')
    );

    this.bindEvents();
  }

  bindEvents() {
    this.openButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.closeBtn?.addEventListener('click', () => this.close());

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) {
        this.close();
      }
    });

    this.confirmRow?.addEventListener('click', () => this.toggleConfirm());
    this.confirmCheck?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleConfirm();
      }
    });

    this.submitBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      if (!this.submitBtn.disabled) this.submit();
    });

    [this.nameInput, this.emailInput, this.phoneInput].forEach((el) => {
      el?.addEventListener('input', () => this.validateForm());
    });
  }

  /**
   * Collect all filled selections from the product info column.
   * Uses the hidden `properties[...]` inputs (populated by every custom
   * block — delivery date, delivery time, ode to, custom text fields, etc.)
   * as the source of truth, plus a few extras read from DOM.
   */
  collectFields() {
    const fields = [];

    // Variant option — from the variant label (e.g. "SIZE — M — 35cm" or "ARRANGEMENT — Large")
    const sizeLabel = document.querySelector('.ode-size-label');
    if (sizeLabel) {
      const optionName = sizeLabel.getAttribute('data-option-name') || 'Size';
      const prefix = new RegExp('^' + optionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*[—-]\\s*', 'i');
      const sizeText = sizeLabel.textContent.replace(prefix, '').trim();
      if (sizeText) fields.push({ label: optionName, value: sizeText });
    }

    // Starting price — from the product info price block
    const priceEl = document.querySelector(
      '.product__info-main_content .price-wrapper .price-item--sale, ' +
      '.product__info-main_content .price-wrapper .price-item--regular, ' +
      '.product__info-main_content .price-wrapper .price-item'
    );
    if (priceEl) {
      const priceText = priceEl.textContent.trim();
      if (priceText) fields.push({ label: 'Starting from', value: priceText });
    }

    // Vase add-on — only if ticked
    const vaseCheckedEl = document.querySelector('.ode-vase-checkbox[aria-checked="true"]');
    if (vaseCheckedEl) {
      const vaseWrapper = vaseCheckedEl.closest('.ode-vase-toggle');
      const vaseLabel = vaseWrapper?.querySelector('.ode-vase-label')?.textContent?.trim() || 'Vase add-on';
      const vasePrice = vaseWrapper?.querySelector('.ode-vase-price')?.textContent?.trim();
      fields.push({ label: 'Vase add-on', value: vasePrice || vaseLabel });
    }

    // All hidden line-item-property inputs — Delivery Date, Delivery Time,
    // Ode To, Gift Message, Colour Preference, etc. Empty inputs are skipped.
    // The product section snippet is rendered TWICE (desktop gallery +
    // mobile gallery), so each `properties[...]` input exists twice in the
    // DOM. Dedupe by property name — first non-empty value wins.
    const seenProps = new Set();
    const propInputs = document.querySelectorAll('input[name^="properties["]');
    propInputs.forEach((input) => {
      const val = (input.value || '').trim();
      if (!val) return;
      // Skip __* private properties (Shopify convention for hidden props)
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      const propName = match[1];
      if (propName.startsWith('_')) return;
      if (seenProps.has(propName)) return;
      seenProps.add(propName);
      fields.push({ label: propName, value: val });
    });

    return fields;
  }

  open() {
    const fields = this.collectFields();
    if (fields.length > 0) {
      this.summaryContainer.innerHTML = fields
        .map(
          (f) =>
            '<div class="ode-summary-row">' +
            '<span class="ode-summary-label">' + this.escapeHtml(f.label) + '</span>' +
            '<span class="ode-summary-value">' + this.escapeHtml(f.value) + '</span>' +
            '</div>'
        )
        .join('');
    } else {
      this.summaryContainer.innerHTML =
        '<div class="ode-summary-empty">No selections yet — you can still send us a request and we\'ll be in touch.</div>';
    }

    this.confirmed = false;
    this.confirmCheck?.setAttribute('aria-checked', 'false');
    this.submitBtn.disabled = true;
    this.formState.style.display = 'block';
    this.successState?.classList.remove('is-visible');
    clearTimeout(this.autoCloseTimer);

    this.overlay.classList.add('is-open');
    document.body.classList.add('ode-quote-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.nameInput?.focus(), 50);
  }

  close() {
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('ode-quote-open');
    document.body.style.overflow = '';
    clearTimeout(this.autoCloseTimer);
  }

  toggleConfirm() {
    this.confirmed = !this.confirmed;
    this.confirmCheck?.setAttribute('aria-checked', String(this.confirmed));
    this.validateForm();
  }

  validateForm() {
    const name = this.nameInput?.value?.trim();
    const email = this.emailInput?.value?.trim();
    const phone = this.phoneInput?.value?.trim();
    const emailOk = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    this.submitBtn.disabled = !(this.confirmed && name && emailOk && phone);
  }

  submit() {
    const fields = this.collectFields();
    const name = this.nameInput?.value?.trim() || '';
    const email = this.emailInput?.value?.trim() || '';
    const phone = this.phoneInput?.value?.trim() || '';
    const productTitle =
      document.querySelector('.product__title')?.textContent?.trim() ||
      'Bespoke Bouquet';

    const lines = [];
    lines.push("Hi Studio Ode! I'd like to request a bespoke quotation:");
    lines.push('');
    lines.push('*' + productTitle + '*');
    fields.forEach((f) => {
      lines.push(f.label + ': ' + f.value);
    });
    lines.push('');
    lines.push('*Contact*');
    lines.push('Name: ' + name);
    lines.push('Email: ' + email);
    lines.push('WhatsApp: ' + phone);

    const message = encodeURIComponent(lines.join('\n'));
    const number = this.getWhatsAppNumber();
    const waUrl = 'https://wa.me/' + number + '?text=' + message;

    // Switch to success state
    this.formState.style.display = 'none';
    this.successState?.classList.add('is-visible');

    // Open WhatsApp in a new tab
    window.open(waUrl, '_blank', 'noopener,noreferrer');

    // Auto-close after 4 seconds
    clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = setTimeout(() => this.close(), 4000);
  }

  getWhatsAppNumber() {
    const raw = this.panel?.dataset.whatsapp || '85295769076';
    return raw.replace(/[^0-9]/g, '');
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }
}

function initOdeBespokeQuote() {
  document.querySelectorAll('.ode-popup-overlay[data-ode-quote-root]').forEach((root) => {
    if (root.__odeQuoteInit) return;
    root.__odeQuoteInit = true;
    new OdeBespokeQuote(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOdeBespokeQuote);
} else {
  initOdeBespokeQuote();
}
